import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { SnackbarProvider } from 'notistack';

import { store } from './store/store';
import theme from './theme/theme';
import Layout from './components/Layout/Layout';
import Dashboard from './pages/Dashboard/Dashboard';
import Schools from './pages/Schools/Schools';
import SchoolDetail from './pages/Schools/SchoolDetail';
import DataImport from './pages/DataImport/DataImport';
import DropoutRiskDashboard from './pages/MLAnalytics/DropoutRiskDashboard';
import TestPrediction from './pages/MLAnalytics/TestPrediction';
import LearningAnalytics from './pages/MLAnalytics/LearningAnalytics';
import Analytics from './pages/Analytics/Analytics';
import Reports from './pages/Reports/Reports';
import EducationLevelReports from './components/Reports/EducationLevelReports';
import SchoolSummaryReport from './components/Reports/SchoolSummaryReport';
import DataConsistencyDashboard from './components/DataQuality/DataConsistencyDashboard';
import EnrollmentDataQuality from './pages/DataQuality/EnrollmentDataQuality';
import Administration from './pages/Administration/Administration';
import SISIntegration from './pages/SISIntegration/SISIntegration';
import DataSyncManager from './pages/SISIntegration/DataSyncManager';
import DataStatus from './pages/DataStatus/DataStatus';
import DataComparison from './pages/DataStatus/DataComparison';
import Attendance from './pages/Attendance/Attendance';
import { IntegrationsHub } from './pages/Integration';
import Login from './pages/Auth/Login';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AuthStatus from './pages/Debug/AuthStatus';
import Profile from './pages/Profile/Profile';
import IndicatorDashboard from './pages/IndicatorDashboard/IndicatorDashboard';
import VisualizationDashboard from './pages/VisualizationDashboard/VisualizationDashboard';
import AdvancedDataValidation from './components/DataQuality/AdvancedDataValidation';
import SchoolReports from './pages/Reports/SchoolReports';
import SchoolMapReport from './pages/SchoolMapReport/SchoolMapReport';
import ErrorBoundary from './components/ErrorBoundary';
import ZestpKpiDashboard from './pages/ZestpKpi/ZestpKpiDashboard';
import EarlyWarningDashboard from './pages/EarlyWarning/EarlyWarningDashboard';
import TeacherDeploymentDashboard from './pages/TeacherDeployment/TeacherDeploymentDashboard';
import EquityDashboard from './pages/EquityDashboard/EquityDashboard';
import UISTrackerDashboard from './pages/UISTracker/UISTrackerDashboard';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      // Cache data for 5 minutes before considering stale
      // Serve cached data instantly on tab switches / revisits
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Router>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/auth-status" element={<AuthStatus />} />
                <Route path="/" element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Dashboard />} />
                  <Route path="profile" element={<Profile />} />
                  <Route path="schools" element={<Schools />} />
                  <Route path="schools/:schoolId" element={<SchoolDetail />} />
                  <Route path="data-import" element={<DataImport />} />
                  <Route path="analytics" element={<Analytics />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="reports/education-levels" element={<EducationLevelReports />} />
                  <Route path="reports/school-summary" element={<SchoolSummaryReport />} />
                  <Route path="reports/school-reports" element={<SchoolReports />} />
                  <Route path="data-quality" element={<DataConsistencyDashboard />} />
                  <Route path="data-quality/enrollment" element={<EnrollmentDataQuality />} />
                  <Route path="attendance" element={<Attendance />} />
                  <Route path="data-status" element={<DataStatus />} />
                  <Route path="data-comparison" element={<DataComparison />} />
                  <Route path="integrations" element={<IntegrationsHub />} />
                  <Route path="ml-analytics/dropout-risk" element={<DropoutRiskDashboard />} />
                  <Route path="ml-analytics/test-prediction" element={<TestPrediction />} />
                  <Route path="ml-analytics/learning" element={<LearningAnalytics />} />
                  <Route path="indicators" element={<IndicatorDashboard />} />
                  <Route path="visualization" element={<VisualizationDashboard />} />
                  <Route path="school-map" element={<SchoolMapReport />} />
                  <Route path="data-quality/advanced" element={<AdvancedDataValidation />} />
                  {/* FMSSEdu Concept Note - Reform Dashboards */}
                  <Route path="zestp-kpis" element={<ZestpKpiDashboard />} />
                  <Route path="early-warning" element={<EarlyWarningDashboard />} />
                  <Route path="teacher-deployment" element={<TeacherDeploymentDashboard />} />
                  <Route path="equity-dashboard" element={<EquityDashboard />} />
                  <Route path="uis-tracker" element={<UISTrackerDashboard />} />
                  {/* Legacy routes for backward compatibility */}
                  <Route path="sis-integration" element={<SISIntegration />} />
                  <Route path="data-sync" element={<DataSyncManager />} />
                  <Route path="integration" element={<IntegrationsHub />} />
                  <Route path="administration" element={<Administration />} />
                </Route>
              </Routes>
            </Router>
          </SnackbarProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </Provider>
    </ErrorBoundary>
  );
}

export default App;